import { deleteApi, getApi, postApi } from 'api/fetch'
import { parseISO } from 'date-fns'
import { useAsync } from 'hooks/use-async'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import { formatDateForDisplayFriendly } from 'utils/date'
import orderBy from 'lodash/orderBy'

import './GhgLandingPage.scss'
import { Farmer, GhgCalculation } from 'api/models'
import { ghgFormLocalStorageKey, LocalStorageState } from '../ghg-calculator.state'
import { UseAsyncState } from '../../hooks/use-async'
import { yupSeasonYear } from '../../utils/validation'
import { Button } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrashAlt, faCopy } from '@fortawesome/free-solid-svg-icons'
import { DeleteCalculationModal } from './DeleteCalculationModal'
import { ExportPDFButton } from './ExportPDFButton'

const fetchCalculationsFn = () => getApi('/api/ghg/v1/calculation')

interface Props {
  user: Farmer
}
export const GhgLandingPage = (props: Props) => {
  const {
    user: { id: userId },
  } = props
  const [calculations, fetchCalculations] = useAsync(fetchCalculationsFn)
  useEffect(() => {
    fetchCalculations().catch(() => {})
  }, [fetchCalculations])

  const [draftCalculation, setDraftCalculation] = useState<DraftCalculationItem | null>(null)
  useEffect(() => {
    try {
      const storedDraftJson = localStorage.getItem(ghgFormLocalStorageKey(userId))
      if (!storedDraftJson) {
        return
      }
      const storedDraft = JSON.parse(storedDraftJson) as LocalStorageState
      setDraftCalculation(storedDraft)
    } catch (e) {
      // do nothing
    }
  }, [userId])

  const handleCopyCalculation = useCallback(
    async (calculation: GhgLandingPageCalculationListItem) => {
      await postApi(
        `/api/ghg/v1/calculation/${calculation.id}/copy` as '/api/ghg/v1/calculation/:id/copy',
        {}
      )
      fetchCalculations().catch(() => {})
    },
    [fetchCalculations]
  )

  const handleDeleteCalculation = useCallback(
    async (calculation: GhgLandingPageCalculationListItem) => {
      if (calculation.id === null) {
        localStorage.removeItem(ghgFormLocalStorageKey(userId))
        setDraftCalculation(null)
      } else {
        await deleteApi(
          `/api/ghg/v1/calculation/${calculation.id}` as '/api/ghg/v1/calculation/:id'
        )
        fetchCalculations().catch(() => {})
      }
    },
    [fetchCalculations, userId]
  )

  return (
    <GhgLandingPageView
      calculations={calculations}
      draftCalculation={draftCalculation}
      onCopyCalculation={handleCopyCalculation}
      onDeleteCalculation={handleDeleteCalculation}
    />
  )
}

// Data for each item in the list of calculations presented to the user.
// Includes their actual calculations (stored on the server) as well as
// optionally one entry representing draft data in LocalStorage
export interface GhgLandingPageCalculationListItem {
  id: number | null
  lastUpdated: string
  lastUpdatedFriendly: string
  name: string
  copy: boolean
}

function formatSeasonYear(seasonYear: number): string {
  return `(${seasonYear}-${seasonYear + 1})`
}

interface DraftCalculationItem {
  draftModifiedAt: string
  farmDetail: Pick<LocalStorageState['farmDetail'], 'season_year' | 'name'>
}

interface ViewProps {
  calculations: UseAsyncState<GhgCalculation[]>
  draftCalculation: DraftCalculationItem | null
  onCopyCalculation: (calculation: GhgLandingPageCalculationListItem) => Promise<void>
  onDeleteCalculation: (calculation: GhgLandingPageCalculationListItem) => Promise<void>
  // This prop is mainly for Storybook, to make it easier to display the
  // deletion success message.
  initialDeletedCalculation?: GhgLandingPageCalculationListItem
}

export const GhgLandingPageView = (props: ViewProps) => {
  const {
    calculations,
    draftCalculation,
    initialDeletedCalculation,
    onCopyCalculation,
    onDeleteCalculation,
  } = props

  const [
    lastDeletedCalculation,
    setLastDeletedCalculation,
  ] = useState<GhgLandingPageCalculationListItem | null>(initialDeletedCalculation ?? null)

  const calculationListItems = useMemo((): GhgLandingPageCalculationListItem[] => {
    const allCalcs: GhgLandingPageCalculationListItem[] = [
      ...(calculations.data?.map((c) => ({
        id: c.id,
        lastUpdated: c.object_modified,
        lastUpdatedFriendly: formatDateForDisplayFriendly(parseISO(c.object_modified)),
        name: `${c.farm.name} ${formatSeasonYear(c.season_year)}`,
        copy: c.copy,
      })) ?? []),
    ]
    if (draftCalculation) {
      // Ignore an invalid season year
      const seasonYear = yupSeasonYear().isValidSync(draftCalculation.farmDetail.season_year)
        ? draftCalculation.farmDetail.season_year
        : null
      allCalcs.push({
        id: null,
        lastUpdated: draftCalculation.draftModifiedAt,
        lastUpdatedFriendly: formatDateForDisplayFriendly(
          parseISO(draftCalculation.draftModifiedAt)
        ),
        name: [
          draftCalculation.farmDetail.name || 'Unnamed farm',
          ...(seasonYear ? [formatSeasonYear(seasonYear)] : []),
        ].join(' '),
        copy: false,
      })
    }
    return orderBy(allCalcs, (iter) => iter.lastUpdated, 'desc')
  }, [calculations.data, draftCalculation])

  const [
    calculationToDelete,
    setCalculationToDelete,
  ] = useState<GhgLandingPageCalculationListItem | null>(null)

  const handleCloseModal = useCallback(() => {
    setCalculationToDelete(null)
  }, [])

  const handleCopyCalc = useCallback(
    async (calc: GhgLandingPageCalculationListItem) => {
      await onCopyCalculation(calc)
    },
    [onCopyCalculation]
  )

  const handleDeleteCalc = useCallback(
    async (calc: GhgLandingPageCalculationListItem) => {
      await onDeleteCalculation(calc)
      setLastDeletedCalculation(calc)
      setCalculationToDelete(null)
    },
    [onDeleteCalculation]
  )

  return (
    <>
      <div className="row justify-content-center bg-light mb-5">
        <div className="col-10">
          <h1 className="text-center mt-5">Greenhouse Gas Emissions Calculation</h1>
        </div>
      </div>

      <div className="row justify-content-center">
        <div className="col-10 text-center">
          <h2>Start a new emissions calculation</h2>
          <Link to="/new" className="btn btn-primary btn-lg mt-3" role="button">
            {draftCalculation ? 'Continue' : 'Start new'}
          </Link>

          {calculations.loading ? (
            <div className="mt-5">
              <Spinner animation="border" role="status">
                <span className="sr-only">Loading...</span>
              </Spinner>
            </div>
          ) : calculations.error ? (
            <h3 className="h6 font-weight-bold text-danger mt-5">
              Oops! Something went wrong while fetching previous calculations.
            </h3>
          ) : (
            <>
              <div data-testid="previous-calculations" className="mt-5">
                {lastDeletedCalculation && (
                  <h3 className="h6 font-weight-bold text-success">
                    Your entry for {lastDeletedCalculation.name} from{' '}
                    {lastDeletedCalculation.lastUpdatedFriendly} has been deleted.
                  </h3>
                )}
                <h3 className="h6 font-weight-bold">
                  View previously completed emissions calculations
                </h3>
                {calculationListItems.length ? (
                  <table className="calculation-list">
                    <tbody>
                      {calculationListItems.map((calc) => (
                        <tr className="mt-1" key={`${calc.id}`}>
                          <td className="pr-3">
                            {calc.id && (
                              <ExportPDFButton
                                variant="outline-secondary btn-icon mr-2"
                                aria-label="Export PDF"
                                title="Export PDF"
                                data-testid="export-button"
                                calculationId={calc.id}
                              />
                            )}
                            {calc.id && (
                              <Button
                                variant="outline-secondary btn-icon mr-2"
                                onClick={() => {
                                  handleCopyCalc(calc)
                                }}
                                aria-label="Copy"
                                title="Copy"
                                data-testid="copy-button"
                              >
                                <FontAwesomeIcon icon={faCopy} />
                              </Button>
                            )}
                          </td>
                          <td className="calculation-link">
                            <Link to={calc.id ? `/edit/${calc.id}` : '/new'}>
                              {calc.name} - {calc.lastUpdatedFriendly}
                              {calc.id === null && ' (draft)'}
                              {calc.copy && ' (copy)'}
                            </Link>
                          </td>
                          <td className="pl-4">
                            <Button
                              variant="outline-secondary btn-icon mr-3"
                              onClick={() => {
                                // Clear any "You have just deleted..." success message
                                setLastDeletedCalculation(null)
                                // Open the confirmation modal
                                setCalculationToDelete(calc)
                              }}
                              aria-label="Delete"
                              title="Delete"
                              data-testid="delete-button"
                            >
                              <FontAwesomeIcon icon={faTrashAlt} />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="mt-1">You haven't created any calculations.</p>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      {calculationToDelete && (
        <DeleteCalculationModal
          calc={calculationToDelete}
          onClose={handleCloseModal}
          onSubmit={handleDeleteCalc}
        />
      )}
    </>
  )
}
